import useTemplates from "@/composables/v3/useTemplates";
import { useUploadAttachmentsStore } from "@/store/useUploadAttachmentsStore";
import { useChannelsStore } from "@/store/useChannelsStore";
import { getChannelSettingValue, resolveAutomaticSignature } from "@/utils/channels";
import { computed, nextTick, ref } from "@vue/composition-api";
import { getUserData } from "@/auth/getUserData";

const isSendPending = ref(false);
const message = ref("");
const messageRef = ref(null);
const shallShowEmojiPicker = ref(false);

export default function useChatInput() {
    const attachmentsStore = useUploadAttachmentsStore();
    const channelsStore = useChannelsStore();

    const selectEmoji = (emoji: { data: string }): void => {
        // 1. Get the message element
        const messageElement = messageRef.value;

        if (!messageElement) {
            return;
        }

        // 2. Set the element in focus
        // @ts-ignore
        messageElement.focus();

        // 3. Get location of the caret
        // @ts-ignore
        const caretLocation = messageElement.selectionStart;

        // 4. Place emoji in location of caret
        message.value = `${message.value.slice(0, caretLocation)}${emoji.data}${message.value.slice(caretLocation)}`;

        // 5. Place caret after newly placed emoji (since some emojis are longer than 1 char we need to add emoji.length)
        const prevMessage = message.value;

        nextTick(() => {
            let pos = caretLocation + emoji.data.length;

            // if message changes since last tick add difference in lengt to pos
            if (prevMessage !== message.value) {
                pos += message.value.length - prevMessage.length;
            }

            // @ts-ignore
            messageRef.value?.setSelectionRange(pos, pos);
        });

        shallShowEmojiPicker.value = false;
    };

    // Reset all input like you've pressed F5.
    const resetInput = (): void => {
        message.value = "";
        attachmentsStore.clearAttachments();

        const { resetTemplate } = useTemplates();
        resetTemplate();
    };

    const hasInput = computed((): boolean => {
        return Boolean(attachmentsStore.hasAttachments || message.value);
    });

    const maxMessageChars = computed((): number => {
        const automaticSignature =
            getChannelSettingValue(channelsStore.activeChatChannel, "automaticSignature") != "none";

        if (!channelsStore.activeChatChannel) {
            return 0;
        }

        switch (channelsStore.activeChatChannel.platform) {
            case "whatsapp":
            case "test":
            case "telegram":
                return automaticSignature
                    ? 4096 -
                          (getUserData()?.displayName?.length ||
                              0 +
                                  (getChannelSettingValue(channelsStore.activeChatChannel, "automaticSignature") ==
                                  "prepend-agent-name-bold"
                                      ? 6
                                      : 2))
                    : 4096;
            case "sms":
                return 1530; // Plain encoded messages (also for Spryng)
            case "messenger":
                return 20000;
            case "direct_messages":
                return 10000;
            case "instagram":
            case "web":
            case "business_messages":
            default:
                return 1000;
        }
    });

    const getFinalMessageToSend = (): string => {
        const { isTemplate, isQuickReply } = useTemplates();

        if (!isTemplate.value && !isQuickReply.value && message.value && channelsStore.activeChatChannel) {
            return resolveAutomaticSignature(message.value, channelsStore.activeChatChannel);
        }

        return message.value;
    };

    return {
        getFinalMessageToSend,
        resetInput,
        isSendPending,
        message,
        messageRef,
        selectEmoji,
        shallShowEmojiPicker,
        hasInput,
        maxMessageChars,
    };
}
